import { useEffect, useRef, useState } from 'react';

import { TabProps } from 'types/tabs';

const Tabs = ({
  tabs,
  onTabSelect,
  selectedTab,
  className,
  tabClassName,
  countClassName,
  type = 'primary'
}: TabProps) => {
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isSecondary = type === 'secondary';

  useEffect(() => {
    if (selectedTab && tabs && !isSecondary) {
      const selectedIndex = tabs.findIndex((tab) => tab.id === selectedTab.id);
      const selectedTabElement = tabRefs.current[selectedIndex];

      if (selectedTabElement) {
        const { offsetLeft, offsetWidth } = selectedTabElement;
        setUnderlineStyle({
          left: offsetLeft,
          width: offsetWidth
        });
      }
    }
  }, [selectedTab, tabs, isSecondary]);

  const containerClass = isSecondary
    ? `relative flex pt-4 ${className || ''}`
    : `relative flex border-b border-cofeeGreyBlue300 ${className || ''}`;

  const getTabItemClass = (isActive: boolean, index: number) => {
    if (isSecondary) {
      const commonClass = `cursor-pointer border px-4 py-2.5 text-center text-sm font-medium ${
        index === 0 ? 'rounded-l-lg' : ''
      } ${index === tabs.length - 1 ? 'rounded-r-lg' : ''}`;

      return isActive
        ? ` bg-cofeePrimary3 text-cofeeNeutral10 ${commonClass}`
        : `bg-cofeeNeutral1 text-cofeeNeutral10 ${commonClass}`;
    }
    return 'flex-end flex min-w-30 cursor-pointer flex-col md:min-w-180';
  };

  const getTabTextClass = (isActive: boolean) => {
    if (isSecondary) return '';
    return `flex justify-center gap-2 truncate px-4 py-3.5 pb-2 text-center text-base font-medium ${
      isActive ? 'text-cofeePrimary6' : 'text-content'
    }`;
  };

  return (
    <div className={containerClass}>
      {tabs?.map((tab, index) => {
        const isActive = selectedTab?.id === tab.id;

        return isSecondary ? (
          <div
            role="presentation"
            className={`${getTabItemClass(isActive, index)} ${
              tabClassName || ''
            }`}
            key={tab.id}
            onClick={() => {
              onTabSelect?.(tab);
            }}
          >
            {tab.label}
            {(tab.count || tab.count === 0) && (
              <span
                className={`ml-2 rounded-full bg-grey90 px-3 py-0.5 text-sm font-normal ${
                  countClassName || ''
                }`}
              >
                {tab.count}
              </span>
            )}
          </div>
        ) : (
          <div
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            role="presentation"
            className={getTabItemClass(isActive, index)}
            key={tab.id}
            onClick={() => {
              onTabSelect?.(tab);
            }}
          >
            <div
              className={`${getTabTextClass(isActive)} ${tabClassName || ''}`}
              key={tab.id}
            >
              {tab.label}
              {(tab.count || tab.count === 0) && (
                <div
                  className={`rounded-full bg-grey90 px-4 py-1 text-sm font-normal text-content ${
                    countClassName || ''
                  }`}
                >
                  {tab.count}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {!isSecondary && (
        <div
          className="duration-250 absolute bottom-0 -mb-0.25 h-0.5 bg-cofeePrimary6 transition-all ease-out"
          style={{
            left: `${underlineStyle.left}px`,
            width: `${underlineStyle.width}px`
          }}
        />
      )}
    </div>
  );
};

export default Tabs;
